import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CalendarClock, ChevronRight, X, AlertTriangle } from "lucide-react";

export default function Series() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [series, setSeries] = useState([]);
  const [busy, setBusy] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const refresh = async () => {
    setBusy(true);
    try {
      // Customer: derive series from their bookings
      const r = await api.get("/bookings/me");
      const map = {};
      for (const b of r.data) {
        if (!b.series_id) continue;
        if (!map[b.series_id]) {
          map[b.series_id] = {
            series_id: b.series_id,
            frequency: b.series_frequency,
            total_occurrences: b.series_total,
            vehicle_name: b.vehicle_name,
            pickup_address: b.pickup_address,
            dropoff_address: b.dropoff_address,
            fare_per_ride: b.fare,
            bookings: [],
          };
        }
        map[b.series_id].bookings.push(b);
      }
      const list = Object.values(map).map(s => {
        s.bookings.sort((a, b) => a.pickup_datetime.localeCompare(b.pickup_datetime));
        s.first_pickup = s.bookings[0]?.pickup_datetime;
        s.last_pickup = s.bookings[s.bookings.length - 1]?.pickup_datetime;
        s.active_count = s.bookings.filter(b => ["new","assigned","en_route","in_progress"].includes(b.status)).length;
        s.completed_count = s.bookings.filter(b => b.status === "completed").length;
        s.cancelled_count = s.bookings.filter(b => b.status === "cancelled").length;
        return s;
      });
      list.sort((a, b) => (b.first_pickup || "").localeCompare(a.first_pickup || ""));
      setSeries(list);
    } catch (e) { /* empty */ } finally { setBusy(false); }
  };

  const openSeries = async (s) => {
    setOpenId(s.series_id);
    setDetail(s);
  };

  const cancelSeries = async (sid) => {
    if (!window.confirm("Cancel ALL future rides in this series? Completed ones stay.")) return;
    try {
      const r = await api.delete(`/bookings/series/${sid}`);
      toast.success(`${r.data.cancelled} future rides cancelled`);
      setOpenId(null); setDetail(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!user) return null;

  return (
    <div data-testid="series-page" className="bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar/>
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="overline mb-3">My recurring series</div>
          <h1 className="font-serif text-4xl md:text-5xl leading-none mb-3">Manage your standing rides.</h1>
          <p className="text-[#A1A1AA] mb-10 max-w-2xl">View, track or cancel future rides in any of your weekly, biweekly or monthly series.</p>

          {busy && <div className="text-[#A1A1AA]">Loading…</div>}
          {!busy && series.length === 0 && (
            <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center">
              <CalendarClock size={32} className="text-[#D4AF37] mx-auto mb-4"/>
              <div className="font-serif text-2xl text-[#F5F2EB] mb-2">No recurring series yet.</div>
              <p className="text-[#A1A1AA] text-sm mb-6 max-w-md mx-auto">Book a ride and check "Make this a recurring ride" to start a standing schedule.</p>
              <button data-testid="series-new-booking" onClick={() => nav("/book")} className="btn-gold">Book a new ride</button>
            </div>
          )}

          <div className="space-y-4">
            {series.map(s => (
              <button
                key={s.series_id}
                data-testid={`series-row-${s.series_id}`}
                onClick={() => openSeries(s)}
                className="w-full text-left border border-white/10 bg-[#0A0A0A] p-6 flex flex-col md:flex-row md:items-center gap-6 hover:border-[#D4AF37]/40 transition"
              >
                <div className="flex-1">
                  <div className="overline mb-1">{s.frequency} series · {s.total_occurrences} rides</div>
                  <div className="font-serif text-xl mb-1">{s.vehicle_name}</div>
                  <div className="text-sm text-[#A1A1AA]">{s.pickup_address} → {s.dropoff_address}</div>
                  <div className="text-xs text-[#71717A] mt-1">
                    {new Date(s.first_pickup).toLocaleDateString()} → {new Date(s.last_pickup).toLocaleDateString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-xl text-[#D4AF37]">${s.fare_per_ride.toFixed(2)}<span className="text-xs text-[#71717A]"> / ride</span></div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mt-1">
                    {s.active_count} active · {s.completed_count} done {s.cancelled_count ? `· ${s.cancelled_count} cxl` : ""}
                  </div>
                </div>
                <ChevronRight className="text-[#D4AF37]"/>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => { setDetail(null); setOpenId(null); }}/>
          <div className="relative w-full max-w-3xl max-h-[90vh] bg-[#0A0A0A] border border-white/10 rounded-lg overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="sticky top-0 bg-[#0A0A0A] z-10 p-6 border-b border-white/10 flex justify-between">
              <div>
                <div className="overline">Recurring series</div>
                <div className="font-serif text-2xl mt-1">{detail.vehicle_name}</div>
                <div className="text-xs text-[#71717A] mt-1">{detail.total_occurrences} {detail.frequency} rides</div>
              </div>
              <button data-testid="series-drawer-close" onClick={() => { setDetail(null); setOpenId(null); }} className="text-[#A1A1AA]"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-[#A1A1AA]">{detail.pickup_address} → {detail.dropoff_address}</div>
              <div className="border-t border-white/10 pt-4 space-y-2">
                {detail.bookings.map(b => (
                  <div key={b.booking_id} className="flex items-center justify-between text-sm py-2 border-b border-white/5 last:border-b-0">
                    <div className="flex-1">
                      <div className="overline">{b.confirmation_code}</div>
                      <div className="text-[#F5F2EB] text-xs mt-0.5">{new Date(b.pickup_datetime).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">{b.status}</div>
                      {["assigned","en_route","in_progress"].includes(b.status) && (
                        <a href={`/track?code=${b.confirmation_code}&email=${encodeURIComponent(b.customer_email)}`} className="text-[10px] text-[#A1A1AA] hover:text-[#D4AF37] uppercase tracking-[0.18em]">Track</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button
                data-testid="cancel-series-btn"
                onClick={() => cancelSeries(detail.series_id)}
                className="btn-ghost !text-red-400 !border-red-400/30 w-full mt-4 justify-center"
              ><AlertTriangle size={14}/> Cancel all future rides</button>
            </div>
          </div>
        </div>
      )}
      <Footer/>
    </div>
  );
}
